import styled from '@emotion/styled';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, pxToRem, StarRatingPicker } from '@modernhealth/vital-components';
import { CoachNode } from 'src/generated/webapp_gql';
import { color, font } from 'src/shared/styles/variables';
import { RateSessionProviderAvatar } from './RateSessionProviderAvatar';

const QuestionText = styled.h4(
    {
        color: color.ebonyClay,
        marginTop: 15,
        marginBottom: 5,
        textAlign: 'center',
    },
    font.black(font.size.extraLarge),
);

const ShareFeedbackText = styled.p(
    {
        color: color.baliHai,
        marginTop: 20,
        textAlign: 'center',
    },
    font.medium(font.size.mediumExtended),
);

const AnswersWrapper = styled.ul({
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: 25,
    padding: 0,
    justifyContent: 'space-between',
    width: '100%',
});

interface FeedbackOptionProps {
    selected: boolean;
}

const AnswerTile = styled.li<FeedbackOptionProps>(
    props => ({
        flex: '0 0 48.5%',
        textAlign: 'center',
        display: 'block',
        marginBottom: 10,
        borderRadius: 4,
        backgroundColor: props.selected ? color.royalBlue : color.catskillWhite,
        cursor: 'pointer',
        padding: '13px 0 10px',
        color: props.selected ? color.white : color.steelGray,
        transition: '.3s all',
        border: `0.5px solid ${props.selected ? color.royalBlue : color.catskillWhite}`,

        ':hover': {
            borderColor: color.royalBlue,
        },
    }),
    font.heavy(font.size.base),
);

interface Props {
    onContinueClick: () => void;
    onRatingChange: (value: number) => void;
    provider: Pick<CoachNode, 'firstName' | 'lastName' | 'avatar'>;
    question: string;
    answersOptions: { text: string; value: string }[];
    selectedAnswers: string[];
    onAnswerClick: (answer: string) => void;
    rating?: number;
    isPamm?: boolean;
}

export const RateSessionTileQuestion: React.FC<Props> = ({
    onContinueClick,
    provider,
    rating,
    onRatingChange,
    onAnswerClick,
    question,
    answersOptions,
    selectedAnswers,
    isPamm,
}) => {
    const { t } = useTranslation();
    return (
        <React.Fragment>
            {!isPamm && <RateSessionProviderAvatar provider={provider} />}
            <ShareFeedbackText>
                {t('videoSessions:rate_session.tile_question.title')}
            </ShareFeedbackText>
            <QuestionText>{question}</QuestionText>
            {!isPamm && <StarRatingPicker defaultRating={rating} onRatingChange={onRatingChange} />}

            <AnswersWrapper>
                {answersOptions.map(({ text, value }) => (
                    <AnswerTile
                        selected={selectedAnswers.includes(value)}
                        onClick={() => onAnswerClick(value)}
                        key={value}
                        data-testid={`answer-${value}`}
                    >
                        {isPamm ? t(text) : t(`videoSessions:${text}`)}
                    </AnswerTile>
                ))}
            </AnswersWrapper>

            <Button onClick={onContinueClick} style={{ marginTop: pxToRem(60) }} fluid>
                {t('common.continue')}
            </Button>
        </React.Fragment>
    );
};
